import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Tag, MapPin, Phone, Check, X, CheckCheck, MessageSquare, Building2, Plus } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import { QUOTE_STATUS, fmtDate, fmtDistance, fmtKm, fmtTry } from "../lib/labels";
import { QuoteLifecycleNote, QuoteCountdown } from "../components/QuoteLifecycle";

export default function Tekliflerim() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/teklif/taleplerim").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  const act = async (tid, action, offerId) => {
    setBusy(action + tid);
    try {
      await api.post(`/teklif/talep/${tid}/${action}`, offerId ? { offer_id: offerId } : {});
      toast.success({ iletisim: "İletişim bilginiz işletmeyle paylaşıldı", kabul: "Teklif kabul edildi", tamamla: "Talep tamamlandı", iptal: "Talep iptal edildi" }[action] || "Güncellendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  return (
    <div data-testid="tekliflerim-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Tag className="w-6 h-6 text-emerald-300" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Teklif Taleplerim</h1>
          </div>
          <Link data-testid="tekliflerim-new" to="/teklif-al"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors">
            <Plus className="w-4 h-4" /> Yeni Talep
          </Link>
        </div>
        <QuoteLifecycleNote testid="tekliflerim-lifecycle-note" />

        {items === null ? (
          <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-10 text-center">
            <p className="text-sm text-slate-500">Henüz teklif talebiniz yok.</p>
            <Link to="/teklif-al" className="text-sm text-emerald-300 hover:text-emerald-200 font-semibold">İlk talebinizi oluşturun</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((r) => {
              const st = QUOTE_STATUS[r.status] || QUOTE_STATUS.bekliyor;
              const open = ["bekliyor", "teklif_geldi", "gorusuluyor"].includes(r.status);
              return (
                <div key={r.id} data-testid={`quote-request-${r.id}`}
                  className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-100">{r.category_label}</p>
                      {r.tow_scope && (
                        <span data-testid={`tow-scope-badge-${r.id}`} className="inline-block mt-1 text-[10px] px-2.5 py-1 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 font-bold">
                          {r.tow_scope === "sehirler_arasi" ? "ŞEHİRLER ARASI ÇEKİCİ" : "ŞEHİR İÇİ ÇEKİCİ"}
                        </span>
                      )}
                      {r.tow_scope === "sehirler_arasi" && r.from_city && (
                        <p data-testid={`tow-route-${r.id}`} className="text-xs text-cyan-300 mt-1">Güzergâh: {r.from_city} / {r.from_district} → {r.to_city} / {r.to_district}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.year && ` • ${r.year}`}{r.km != null && ` • ${fmtKm(r.km)}`}{r.plate && ` • ${r.plate}`}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {[r.city, r.district].filter(Boolean).join(" / ")} • {fmtDate(r.created_at)}
                      </p>
                      {r.part_name && <p className="text-xs text-cyan-300 mt-1">Parça: {r.part_name}</p>}
                      {r.expected_price != null && <p className="text-xs text-cyan-300 mt-1">Fiyat beklentisi: {fmtTry(r.expected_price)}</p>}
                      {(r.dossier_views || []).length > 0 && (
                        <p data-testid={`dossier-view-count-${r.id}`} className="text-xs text-emerald-300 mt-1">
                          Dosyanız {r.dossier_views.length} işletme tarafından incelendi
                        </p>
                      )}
                      {r.description && <p className="text-xs text-slate-400 mt-1.5">{r.description}</p>}
                      {r.photos && r.photos.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {r.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span data-testid={`quote-status-${r.id}`} className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                      <span className="text-[11px] text-slate-500">{r.offers_count || 0} teklif</span>
                      <QuoteCountdown createdAt={r.created_at} testid={`quote-countdown-${r.id}`} />
                    </div>
                  </div>

                  {r.offers && r.offers.length > 0 && (
                    <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
                      {r.offers.map((o) => (
                        <div key={o.id} data-testid={`quote-offer-${o.id}`}
                          className={`rounded-xl border px-4 py-3.5 ${o.status === "kabul" ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-slate-950/50"}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-slate-100 flex items-center gap-1.5">
                                <Building2 className="w-4 h-4 text-emerald-300" /> {o.business_name}
                                {o.business_type_label && <span className="text-[10px] text-slate-500 font-normal">{o.business_type_label}</span>}
                              </p>
                              <p className="text-lg font-bold text-emerald-300 mt-1">{o.price != null ? fmtTry(o.price) : "Fiyat belirtilmedi"}</p>
                              <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                                <p>Teklif tarihi: {fmtDate(o.created_at)}</p>
                                {o.mesafe_km != null && <p>{fmtDistance(o.mesafe_km)}</p>}
                                {o.eta && <p>Tahmini süre: {o.eta}</p>}
                                {o.valid_until && <p data-testid={`quote-offer-validity-${o.id}`}>Geçerlilik: {fmtDate((o.valid_until || "").slice(0, 10))}</p>}
                                {o.brand_product && <p>Ürün/Marka: {o.brand_product}</p>}
                                {o.warranty && <p>Garanti: {o.warranty}</p>}
                                {o.description && <p className="text-slate-500">{o.description}</p>}
                              </div>
                              <p className="text-xs text-cyan-300 mt-1.5 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" /> {o.phone || "-"}{o.whatsapp && ` • WhatsApp: ${o.whatsapp}`}
                              </p>
                            </div>
                            {open && o.status === "bekliyor" && (
                              <div className="flex flex-col gap-2 shrink-0">
                                <button data-testid={`quote-contact-${o.id}`} disabled={!!busy} onClick={() => act(r.id, "iletisim", o.id)}
                                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-400/10 transition-colors disabled:opacity-50">
                                  <MessageSquare className="w-3.5 h-3.5" /> İletişimi Paylaş
                                </button>
                                <button data-testid={`quote-accept-${o.id}`} disabled={!!busy} onClick={() => act(r.id, "kabul", o.id)}
                                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors disabled:opacity-50">
                                  <Check className="w-3.5 h-3.5" /> Teklifi Kabul Et
                                </button>
                              </div>
                            )}
                            {o.status === "kabul" && (
                              <span className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-semibold shrink-0">Kabul Edildi</span>
                            )}
                            {o.status === "reddedildi" && (
                              <span className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-500 shrink-0">Seçilmedi</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex gap-2 flex-wrap">
                    {r.status === "kabul_edildi" && (
                      <button data-testid={`quote-complete-${r.id}`} disabled={!!busy} onClick={() => act(r.id, "tamamla")}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 text-xs font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50">
                        <CheckCheck className="w-3.5 h-3.5" /> İş Tamamlandı
                      </button>
                    )}
                    {open && (
                      <button data-testid={`quote-cancel-${r.id}`} disabled={!!busy}
                        onClick={() => { if (window.confirm("Talebi iptal etmek istediğinize emin misiniz? Gelen teklifler de kapanır.")) act(r.id, "iptal"); }}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-slate-400 text-xs hover:border-red-400/50 hover:text-red-300 transition-colors disabled:opacity-50">
                        <X className="w-3.5 h-3.5" /> Talebi İptal Et
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
